import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiArrowUpRight,
  FiLoader,
  FiAlertTriangle,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiZoomIn,
  FiMessageCircle,
} from "react-icons/fi";

import PageTransition from "./Pagetransition";
import ventana7 from "../assets/ventana7.png";

/* URL base de la API. Ajustá esto a tu dominio real en producción,
   o usá una variable de entorno (Vite: import.meta.env.VITE_API_URL). */
const API_URL =
  import.meta.env?.VITE_API_URL || "https://monacoaberturas.com";

/* Número de WhatsApp del negocio (mismo que usa el CTA general de la página). */
const WHATSAPP_NUMERO = "542236901020";

const easeOut = [0.22, 1, 0.36, 1];

/* ───────────────── ANIMATIONS ───────────────── */

const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.15,
    },
  },
};

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: easeOut,
    },
  },
};

/* ───────────────── HELPERS ───────────────── */

/* Bloquea el scroll del body mientras `activo` sea true (usado por el lightbox) */
function useBloquearScroll(activo) {
  useEffect(() => {
    if (!activo) return;
    const overflowPrevio = document.body.style.overflow;
    const paddingPrevio = document.body.style.paddingRight;

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = overflowPrevio;
      document.body.style.paddingRight = paddingPrevio;
    };
  }, [activo]);
}

/* Arma el link de WhatsApp con la info del producto precargada */
function armarLinkWhatsapp(product) {
  const mensaje = `Hola! Quiero consultar sobre el producto "${product.titulo}". ¿Me pasás precio y disponibilidad?`;
  return `https://wa.me/${WHATSAPP_NUMERO}?text=${encodeURIComponent(mensaje)}`;
}

/* ───────────────── CARD ───────────────── */

function ProductCard({ product, onAbrirImagen }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{
        y: -10,
      }}
      transition={{
        duration: 0.45,
        ease: easeOut,
      }}
      className="group relative overflow-hidden border border-[#c9973a]/10 bg-white/[0.03] backdrop-blur-xl"
    >
      {/* Glow — puramente decorativo, no debe bloquear clicks */}
      <motion.div
        initial={{
          opacity: 0,
        }}
        whileHover={{
          opacity: 1,
        }}
        transition={{
          duration: 0.5,
        }}
        className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-br from-[#c9973a]/10 via-transparent to-[#8b3520]/20"
      />

      {/* Top line — decorativa */}
      <motion.div
        initial={{
          scaleX: 0,
        }}
        whileHover={{
          scaleX: 1,
        }}
        transition={{
          duration: 0.45,
        }}
        className="pointer-events-none absolute left-0 top-0 z-0 h-[1px] w-full origin-left bg-gradient-to-r from-[#c9973a] via-[#f0c96b] to-transparent"
      />

      {/* Number — decorativo */}
      <div className="pointer-events-none absolute right-6 top-4 z-0 text-[5rem] font-bold leading-none text-white/[0.04]">
        {product.num}
      </div>

      <div className="relative z-10 flex h-full flex-col">
        {/* Photo — limpia, sin íconos ni badges encima. Click para zoom/carousel. */}
        <button
          type="button"
          onClick={() => onAbrirImagen(product)}
          className="group/photo relative z-10 mx-7 mt-7 block w-[calc(100%-3.5rem)] cursor-zoom-in overflow-hidden bg-[#0a0908] md:mx-8 md:mt-8 md:w-[calc(100%-4rem)]"
          aria-label={`Ver imagen ampliada de ${product.titulo}`}
        >
          <div className="aspect-[4/3] w-full">
            <motion.img
              src={product.imagen_base64}
              alt={product.titulo}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.6, ease: easeOut }}
              className="h-full w-full object-cover object-center"
              loading="lazy"
            />
          </div>

          {/* Overlay de zoom, aparece solo al hover, no ensucia la foto en reposo */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#0a0908]/0 opacity-0 transition-all duration-300 group-hover/photo:bg-[#0a0908]/30 group-hover/photo:opacity-100">
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#c9973a]/40 bg-[#1a1814]/80 text-lg text-[#f0c96b] backdrop-blur-sm">
              <FiZoomIn />
            </div>
          </div>
        </button>

        {/* Content */}
        <div className="relative z-10 flex flex-1 flex-col p-7 md:p-8">
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <h3
                className="
                  text-[2rem]
                  leading-none
                  text-[#f9f5ef]
                  md:text-[2.3rem]
                "
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 500,
                }}
              >
                {product.titulo}
              </h3>

              <span className="shrink-0 border border-[#c9973a]/30 bg-[#c9973a]/5 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.28em] text-[#f0c96b]">
                {product.badge}
              </span>
            </div>

            <div className="mt-5 h-px w-16 bg-gradient-to-r from-[#c9973a] to-transparent" />

            <p
              className="
                mt-6
                text-[0.98rem]
                leading-8
                text-[#b7aea2]
              "
              style={{
                fontFamily: "'Jost', sans-serif",
                fontWeight: 300,
              }}
            >
              {product.descripcion}
            </p>
          </div>

          {/* Bottom */}
          <div className="mt-10 flex flex-col gap-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-[7px] w-[7px] rounded-full bg-[#c9973a] shadow-[0_0_18px_rgba(201,151,58,0.9)]" />

                <span
                  className="text-[0.72rem] uppercase tracking-[0.3em] text-[#8f8678]"
                  style={{
                    fontFamily: "'Jost', sans-serif",
                  }}
                >
                  Disponible
                </span>
              </div>

              <span
                className="text-[0.72rem] uppercase tracking-[0.25em] text-[#c9973a]"
                style={{
                  fontFamily: "'Jost', sans-serif",
                }}
              >
                Premium
              </span>
            </div>

            {/* Botón comprar / consultar por WhatsApp */}
            <motion.a
              href={armarLinkWhatsapp(product)}
              target="_blank"
              rel="noreferrer"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
              className="group/btn relative z-10 flex cursor-pointer items-center justify-center gap-2.5 overflow-hidden bg-gradient-to-r from-[#8b6220] via-[#c9973a] to-[#f0c96b] px-6 py-3.5 text-[#1a1814] shadow-lg shadow-[#c9973a]/10 transition-shadow hover:shadow-[#c9973a]/25"
            >
              <FiMessageCircle className="text-lg" />
              <span
                className="text-[0.78rem] font-semibold uppercase tracking-[0.25em]"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                Comprar / Consultar
              </span>
              <FiArrowUpRight className="text-base transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
            </motion.a>
          </div>
        </div>
      </div>

      {/* Border hover — decorativo, no debe bloquear clicks del contenido de abajo */}
      <div className="pointer-events-none absolute inset-0 z-20 border border-transparent transition-all duration-500 group-hover:border-[#c9973a]/20" />
    </motion.div>
  );
}

/* ───────────────── LIGHTBOX / CAROUSEL ───────────────── */

function LightboxProductos({ productos, indiceInicial, onClose }) {
  const [indice, setIndice] = useState(indiceInicial);
  const [zoom, setZoom] = useState(false);
  const [direccion, setDireccion] = useState(0);

  useBloquearScroll(true);

  const producto = productos[indice];

  const irAnterior = useCallback(() => {
    setZoom(false);
    setDireccion(-1);
    setIndice((i) => (i - 1 + productos.length) % productos.length);
  }, [productos.length]);

  const irSiguiente = useCallback(() => {
    setZoom(false);
    setDireccion(1);
    setIndice((i) => (i + 1) % productos.length);
  }, [productos.length]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") irAnterior();
      if (e.key === "ArrowRight") irSiguiente();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, irAnterior, irSiguiente]);

  if (!producto) return null;

  const variantesSlide = {
    enter: (dir) => ({ opacity: 0, x: dir >= 0 ? 60 : -60 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir >= 0 ? -60 : 60 }),
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[1200] flex flex-col bg-[#0a0908]/97 backdrop-blur-xl"
      onClick={onClose}
    >
      {/* Header del lightbox */}
      <div
        className="flex items-center justify-between gap-4 px-5 py-4 sm:px-8 sm:py-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <p
            className="text-[0.95rem] text-[#f9f5ef] sm:text-[1.1rem]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            {producto.titulo}
          </p>
          <p
            className="text-[0.65rem] uppercase tracking-[0.25em] text-[#8f8678]"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            {indice + 1} / {productos.length}
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-[#c9973a]/20 text-[#f0c96b] transition-colors hover:bg-[#c9973a]/10"
          aria-label="Cerrar"
        >
          <FiX size={20} />
        </button>
      </div>

      {/* Imagen central con navegación */}
      <div
        className="relative flex flex-1 items-center justify-center overflow-hidden px-3 sm:px-16"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Flecha anterior */}
        <button
          onClick={irAnterior}
          className="absolute left-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[#c9973a]/25 bg-[#1a1814]/70 text-[#f0c96b] backdrop-blur-sm transition-colors hover:bg-[#c9973a]/20 sm:left-4 sm:h-12 sm:w-12"
          aria-label="Producto anterior"
        >
          <FiChevronLeft size={22} />
        </button>

        <div className="relative flex h-full w-full max-w-5xl items-center justify-center py-4">
          <AnimatePresence mode="wait" custom={direccion}>
            <motion.div
              key={producto.id}
              custom={direccion}
              variants={variantesSlide}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: easeOut }}
              className="flex h-full w-full items-center justify-center"
            >
              <img
                src={producto.imagen_base64}
                alt={producto.titulo}
                onClick={() => setZoom((z) => !z)}
                className={`max-h-full max-w-full select-none rounded-sm object-contain shadow-2xl shadow-black/60 transition-transform duration-300 ${
                  zoom
                    ? "scale-[1.7] cursor-zoom-out sm:scale-[2]"
                    : "scale-100 cursor-zoom-in"
                }`}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Flecha siguiente */}
        <button
          onClick={irSiguiente}
          className="absolute right-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[#c9973a]/25 bg-[#1a1814]/70 text-[#f0c96b] backdrop-blur-sm transition-colors hover:bg-[#c9973a]/20 sm:right-4 sm:h-12 sm:w-12"
          aria-label="Producto siguiente"
        >
          <FiChevronRight size={22} />
        </button>
      </div>

      {/* Footer: descripción + CTA de WhatsApp del producto actual */}
      <div
        className="flex flex-col items-center gap-4 px-5 pb-6 pt-2 text-center sm:px-8 sm:pb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <p
          className="max-w-2xl text-[0.85rem] leading-7 text-[#b7aea2]"
          style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
        >
          {producto.descripcion}
        </p>
        <motion.a
          href={armarLinkWhatsapp(producto)}
          target="_blank"
          rel="noreferrer"
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.97 }}
          className="flex items-center gap-2.5 bg-gradient-to-r from-[#8b6220] via-[#c9973a] to-[#f0c96b] px-7 py-3 text-[#1a1814] shadow-lg shadow-[#c9973a]/10"
        >
          <FiMessageCircle className="text-lg" />
          <span
            className="text-[0.75rem] font-semibold uppercase tracking-[0.25em]"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            Comprar / Consultar
          </span>
        </motion.a>

        {/* Miniaturas de navegación rápida, solo en pantallas más grandes */}
        <div className="mt-2 hidden max-w-full gap-2 overflow-x-auto pb-1 sm:flex">
          {productos.map((p, i) => (
            <button
              key={p.id}
              onClick={() => {
                setDireccion(i > indice ? 1 : -1);
                setZoom(false);
                setIndice(i);
              }}
              className={`h-12 w-16 flex-shrink-0 overflow-hidden rounded-sm border transition-all ${
                i === indice
                  ? "border-[#c9973a] opacity-100"
                  : "border-[#c9973a]/15 opacity-50 hover:opacity-80"
              }`}
            >
              <img
                src={p.imagen_base64}
                alt={p.titulo}
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

/* ───────────────── ESTADOS DE CARGA / ERROR ───────────────── */

function EstadoCargando() {
  return (
    <div className="mt-20 flex flex-col items-center justify-center gap-4 py-20 text-[#b7aea2]">
      <FiLoader className="animate-spin text-3xl text-[#c9973a]" />
      <span
        className="text-[0.8rem] uppercase tracking-[0.3em]"
        style={{ fontFamily: "'Jost', sans-serif" }}
      >
        Cargando productos…
      </span>
    </div>
  );
}

function EstadoError({ mensaje, onRetry }) {
  return (
    <div className="mt-20 flex flex-col items-center justify-center gap-4 py-20 text-center text-[#b7aea2]">
      <FiAlertTriangle className="text-3xl text-[#c9973a]" />
      <p style={{ fontFamily: "'Jost', sans-serif" }}>{mensaje}</p>
      <button
        onClick={onRetry}
        className="border border-[#c9973a]/30 px-6 py-2 text-[0.75rem] uppercase tracking-[0.25em] text-[#c9973a] transition-colors hover:bg-[#c9973a]/10"
        style={{ fontFamily: "'Jost', sans-serif" }}
      >
        Reintentar
      </button>
    </div>
  );
}

/* ───────────────── PAGE ───────────────── */

export default function ProductosPage() {
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [indiceLightbox, setIndiceLightbox] = useState(null);

  async function cargarProductos() {
    setCargando(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/productos.php`);
      if (!res.ok) {
        throw new Error("La API respondió con un error.");
      }
      const data = await res.json();
      setProductos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(
        "No pudimos cargar los productos en este momento. Por favor, intentá de nuevo."
      );
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargarProductos();
  }, []);

  function abrirImagen(product) {
    const idx = productos.findIndex((p) => p.id === product.id);
    setIndiceLightbox(idx >= 0 ? idx : 0);
  }

  return (
    <PageTransition>
      <section className="relative min-h-screen overflow-hidden bg-[#0f0d0b] text-white">
        {/* GRID */}
        <div
          className="
            pointer-events-none absolute inset-0
            bg-[linear-gradient(rgba(201,151,58,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(201,151,58,0.04)_1px,transparent_1px)]
            bg-[size:60px_60px]
          "
        />

        {/* GLOW */}
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            h-[45rem]
            w-[45rem]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-[radial-gradient(circle,rgba(201,151,58,0.12)_0%,transparent_70%)]
          "
        />

        {/* TOP GLOW */}
        <div className="pointer-events-none absolute left-[-10rem] top-[-10rem] h-[30rem] w-[30rem] rounded-full bg-[#c9973a]/10 blur-[120px]" />

        {/* BOTTOM GLOW */}
        <div className="pointer-events-none absolute bottom-[-12rem] right-[-10rem] h-[35rem] w-[35rem] rounded-full bg-[#8b3520]/20 blur-[140px]" />

        {/* CONTENT */}
        <div className="relative z-10 mx-auto max-w-7xl px-5 pb-24 pt-28 md:px-8 lg:pt-36">
          {/* HERO */}
          <motion.div
            initial={{
              opacity: 0,
              y: 40,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 1,
              ease: easeOut,
            }}
            className="mx-auto max-w-5xl text-center"
          >
            {/* Eyebrow */}
            <div
              className="
                mb-6
                inline-flex
                items-center
                gap-3
                border
                border-[#c9973a]/20
                bg-[#c9973a]/5
                px-5
                py-2
              "
            >
              <div className="h-2 w-2 rounded-full bg-[#c9973a]" />

              <span
                className="
                  text-[0.68rem]
                  uppercase
                  tracking-[0.45em]
                  text-[#c9973a]
                "
                style={{
                  fontFamily: "'Jost', sans-serif",
                }}
              >
                Calidad · Diseño · Durabilidad
              </span>
            </div>

            {/* Title */}
            <h1
              className="
                text-[4rem]
                leading-[0.9]
                md:text-[7rem]
              "
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 300,
                color: "#f9f5ef",
              }}
            >
              Nuestros
              <br />

              <span
                className="
                  bg-gradient-to-r
                  from-[#f0c96b]
                  via-[#c9973a]
                  to-[#fde68a]
                  bg-clip-text
                  text-transparent
                "
                style={{
                  fontWeight: 700,
                }}
              >
                Productos
              </span>
            </h1>

            {/* Subtitle */}
            <p
              className="
                mx-auto
                mt-8
                max-w-3xl
                text-[1rem]
                leading-9
                text-[#b7aea2]
                md:text-[1.1rem]
              "
              style={{
                fontFamily: "'Jost', sans-serif",
                fontWeight: 300,
              }}
            >
              Fabricamos e instalamos aberturas de aluminio
              premium combinando tecnología, diseño y
              terminaciones de excelencia para transformar
              cada espacio.
            </p>
          </motion.div>

          {/* GRID / ESTADOS */}
          {cargando && <EstadoCargando />}

          {!cargando && error && (
            <EstadoError mensaje={error} onRetry={cargarProductos} />
          )}

          {!cargando && !error && (
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{
                once: true,
                amount: 0.15,
              }}
              className="
                mt-20
                grid
                grid-cols-1
                gap-6
                md:grid-cols-2
                xl:grid-cols-3
              "
            >
              {productos.map((product, i) => (
                <ProductCard
                  key={product.id}
                  product={{ ...product, num: product.num ?? String(i + 1).padStart(2, "0") }}
                  onAbrirImagen={abrirImagen}
                />
              ))}
            </motion.div>
          )}

          {/* CTA */}
          <motion.div
            initial={{
              opacity: 0,
              y: 50,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.9,
            }}
            className="
              relative
              mt-28
              overflow-hidden
              border
              border-[#c9973a]/10
              bg-white/[0.03]
              backdrop-blur-xl
            "
          >
            {/* Glow — decorativo */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#c9973a]/10 via-transparent to-[#8b3520]/20" />

            <div className="relative z-10 flex flex-col items-stretch lg:flex-row">
              {/* CTA photo */}
              <div className="relative h-56 w-full overflow-hidden lg:h-auto lg:w-[38%]">
                <img
                  src={ventana7}
                  alt="Terminación de aberturas Monaco"
                  className="h-full w-full object-cover object-center"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0f0d0b]/80 lg:bg-gradient-to-r lg:from-transparent lg:via-[#0f0d0b]/10 lg:to-[#0f0d0b]" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0f0d0b]/70 via-transparent to-transparent lg:hidden" />
              </div>

              <div className="flex flex-1 flex-col items-center justify-between gap-10 p-8 md:p-14 lg:flex-row">
                <div className="max-w-2xl">
                  <span
                    className="
                      text-[0.72rem]
                      uppercase
                      tracking-[0.4em]
                      text-[#c9973a]
                    "
                    style={{
                      fontFamily: "'Jost', sans-serif",
                    }}
                  >
                    Presupuesto sin cargo
                  </span>

                  <h2
                    className="
                      mt-5
                      text-[3rem]
                      leading-none
                      text-[#f9f5ef]
                      md:text-[4.5rem]
                    "
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontWeight: 400,
                    }}
                  >
                    Transformamos ideas en espacios únicos
                  </h2>

                  <p
                    className="
                      mt-6
                      max-w-xl
                      text-[1rem]
                      leading-8
                      text-[#b7aea2]
                    "
                    style={{
                      fontFamily: "'Jost', sans-serif",
                      fontWeight: 300,
                    }}
                  >
                    Te asesoramos en cada etapa para encontrar
                    la solución perfecta para tu hogar o
                    proyecto arquitectónico.
                  </p>
                </div>

                {/* BUTTON */}
                <motion.a
                  href="https://wa.me/542236901020?text=Hola!%20Quiero%20consultar%20sobre%20productos%20y%20precios."
                  target="_blank"
                  rel="noreferrer"
                  whileHover={{
                    y: -3,
                    boxShadow:
                      "0 18px 50px rgba(201,151,58,0.25)",
                  }}
                  whileTap={{
                    scale: 0.97,
                  }}
                  className="group relative z-10 shrink-0 cursor-pointer overflow-hidden"
                >
                  {/* bg */}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#8b6220] via-[#c9973a] to-[#f0c96b]" />

                  {/* shine */}
                  <motion.div
                    animate={{
                      x: ["-120%", "220%"],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="
                      pointer-events-none
                      absolute
                      inset-y-0
                      w-24
                      rotate-12
                      bg-white/20
                      blur-xl
                    "
                  />

                  <div
                    className="
                      relative
                      z-10
                      flex
                      items-center
                      gap-3
                      px-10
                      py-5
                      text-[#1a1814]
                    "
                  >
                    <span
                      className="
                        text-[0.78rem]
                        font-semibold
                        uppercase
                        tracking-[0.28em]
                      "
                      style={{
                        fontFamily: "'Jost', sans-serif",
                      }}
                    >
                      Solicitar presupuesto
                    </span>

                    <FiArrowUpRight className="text-xl transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {indiceLightbox !== null && (
          <LightboxProductos
            productos={productos}
            indiceInicial={indiceLightbox}
            onClose={() => setIndiceLightbox(null)}
          />
        )}
      </AnimatePresence>
    </PageTransition>
  );
}